import { Spinner, Warning, Tray, CaretRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export function LoadingState({ label = "Loading…", className }) {
  return (
    <div data-testid="loading-state" className={cn("flex flex-col items-center justify-center py-16 text-muted-foreground", className)}>
      <Spinner className="h-7 w-7 animate-spin text-primary" weight="bold" />
      <p className="mt-3 text-sm font-mono uppercase tracking-wide">{label}</p>
    </div>
  );
}

export function ErrorState({ message = "Something went wrong.", onRetry }) {
  return (
    <div data-testid="error-state" className="flex flex-col items-center justify-center border border-destructive/40 bg-destructive/5 py-12 text-center">
      <Warning className="h-7 w-7 text-destructive" weight="bold" />
      <p className="mt-3 max-w-md text-sm text-destructive">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-4 border border-destructive/50 px-4 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10">
          Retry
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", description, message, eyebrow, icon: Icon = Tray, action, hint, className }) {
  const body = description || message; // accept both `description` and legacy `message`
  return (
    <div data-testid="empty-state" className={cn("flex flex-col items-center justify-center border border-dashed border-border bg-card px-6 py-14 text-center", className)}>
      <span className="flex h-14 w-14 items-center justify-center border border-border bg-surface">
        <Icon className="h-7 w-7 text-muted-foreground" weight="regular" />
      </span>
      {eyebrow && <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">{eyebrow}</p>}
      <h3 className={cn("font-heading text-lg font-bold text-foreground", eyebrow ? "mt-1.5" : "mt-5")}>{title}</h3>
      {body && <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">{body}</p>}
      {action && <div className="mt-6">{action}</div>}
      {hint && <p className="mt-4 font-mono text-[10px] uppercase tracking-wide text-muted-foreground/60">{hint}</p>}
    </div>
  );
}

/**
 * GuidedEmptyState — an on-brand "start here" walkthrough for fresh/blank surfaces.
 * Renders an eyebrow + title + intro, then a numbered list of steps (each optionally a
 * clickable CTA row), so a new engagement feels alive and directs the first action.
 * `steps`: [{ label, description?, onClick?, done? }]
 */
export function GuidedEmptyState({ eyebrow = "Getting started", title, description, icon: Icon = Tray, steps = [], className }) {
  return (
    <div data-testid="guided-empty-state" className={cn("border border-dashed border-border bg-card px-6 py-10 sm:px-10", className)}>
      <div className="mx-auto max-w-xl text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center border border-border bg-surface">
          <Icon className="h-7 w-7 text-primary" weight="regular" />
        </span>
        {eyebrow && <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">{eyebrow}</p>}
        <h3 className="mt-1.5 font-heading text-xl font-bold text-foreground">{title}</h3>
        {description && <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{description}</p>}
      </div>
      {steps.length > 0 && (
        <ol className="mx-auto mt-6 max-w-xl space-y-2" data-testid="guided-empty-steps">
          {steps.map((s, i) => {
            const interactive = typeof s.onClick === "function";
            const Tag = interactive ? "button" : "div";
            return (
              <Tag key={i} onClick={s.onClick} type={interactive ? "button" : undefined}
                data-testid={`guided-step-${i}`}
                className={cn(
                  "group flex w-full items-center gap-3 border border-border bg-surface px-4 py-3 text-left",
                  interactive && "cursor-pointer transition-colors hover:border-accent-ai/40 hover:bg-secondary"
                )}>
                <span className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center font-mono text-xs font-bold",
                  s.done ? "bg-success/10 text-success" : "bg-primary/5 text-primary"
                )}>{s.done ? "✓" : i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">{s.label}</span>
                  {s.description && <span className="mt-0.5 block text-xs text-muted-foreground">{s.description}</span>}
                </span>
                {interactive && <CaretRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" weight="bold" />}
              </Tag>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Skeleton — a soft shimmering placeholder block. Compose for content-aware loading states. */
export function Skeleton({ className }) {
  return <div data-testid="skeleton" className={cn("skeleton rounded-sm", className)} />;
}

/** SkeletonText — N shimmering lines (last line shorter), matching the body type scale. */
export function SkeletonText({ lines = 3, className }) {
  return (
    <div className={cn("space-y-2.5", className)} data-testid="skeleton-text">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

/** SkeletonCard — a bordered panel of shimmering lines; use as a card/list placeholder. */
export function SkeletonCard({ lines = 3, className }) {
  return (
    <div className={cn("border border-border bg-card p-5 shadow-elev-sm", className)} data-testid="skeleton-card">
      <Skeleton className="mb-4 h-4 w-1/3" />
      <SkeletonText lines={lines} />
    </div>
  );
}
